import React, { useState } from 'react';
import { useProducts } from '../../context/ProductsContext';
import { useToast } from '../Common/Toast';
import { getWeightedHours } from '../../utils/calculations';
import Card from '../Common/Card';
import Button from '../Common/Button';

const ProductList = () => {
  const { products, deleteProduct, toggleProduct, loading } = useProducts();
  const { showToast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDelete = async (product) => {
    if (window.confirm(`Are you sure you want to delete "${product.name}"?`)) {
      const result = await deleteProduct(product.id);
      if (result.success) {
        showToast(`Product "${product.name}" deleted successfully!`);
      } else {
        showToast('Failed to delete product', 'error');
      }
    }
  };

  const handleToggle = async (product) => {
    const result = await toggleProduct(product.id, !product.enabled);
    if (!result.success) {
      showToast('Failed to toggle product', 'error');
    }
  };

  if (loading) {
    return (
      <Card title="📋 Your Activities">
        <div className="text-center py-8 text-gray-600">Loading activities...</div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-gray-900">
          📋 Your Activities
          <span className="ml-2 text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
            {products.length}
          </span>
        </h2>
      </div>

      <input
        type="text"
        placeholder="Search activities..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />

      {filteredProducts.length === 0 ? (
        <div className="text-center py-8 text-gray-600">
          {searchTerm ? 'No activities found matching your search.' : 'No activities yet. Add your first activity above!'}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredProducts.map(product => (
            <div
              key={product.id}
              className={`border rounded-lg p-4 transition-all ${
                product.enabled !== false
                  ? 'border-gray-300 bg-white'
                  : 'border-gray-300 bg-gray-100'
              }`}
            >
              {/* Title */}
              <h3 className={`font-semibold text-base break-words leading-tight mb-2 ${
                product.enabled !== false ? 'text-gray-900' : 'text-gray-700'
              }`}>
                {product.name}
              </h3>

              {/* Hours Information */}
              <div className={`flex gap-4 text-sm mb-3 ${
                product.enabled !== false ? 'text-gray-600' : 'text-gray-700'
              }`}>
                <div>
                  <span className="font-medium">Base:</span> {product.baseHours} hrs
                </div>
                <div>
                  <span className="font-medium">Weighted:</span> {Math.round(getWeightedHours(product))} hrs
                </div>
              </div>

              {/* Scoring Percentages */}
              <div className="grid grid-cols-3 gap-4 text-sm mb-3 pb-3 border-b border-gray-200">
                <div>
                  <div className="text-gray-600 text-xs mb-1">Complexity</div>
                  <div className="font-semibold text-gray-900">{product.comp}%</div>
                </div>
                <div>
                  <div className="text-gray-600 text-xs mb-1">Risk</div>
                  <div className="font-semibold text-gray-900">{product.risk}%</div>
                </div>
                <div>
                  <div className="text-gray-600 text-xs mb-1">Environment</div>
                  <div className="font-semibold text-gray-900">{product.env}%</div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 justify-end">
                <Button
                  size="sm"
                  variant={product.enabled !== false ? 'secondary' : 'outline'}
                  onClick={() => handleToggle(product)}
                >
                  {product.enabled !== false ? '✓ Enabled' : '○ Disabled'}
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => handleDelete(product)}
                >
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default ProductList;
